import * as Sentry from '@sentry/react';
import { devLogger } from './dev-logger';

export class GeminiAudioManager {
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private audioQueue: ArrayBuffer[] = [];
  private readonly MAX_QUEUE = 800; // cap to avoid memory bloat
  private isPlaying = false;
  private nextStartTime = 0;
  private isMuted = false;
  private isSoftMuted = false;
  private pendingResponseCompletion = false;
  private currentAudioSources: AudioBufferSourceNode[] = [];
  private workletModuleLoaded = false;
  private frameSink: ((data: ArrayBuffer) => void) | null = null;
  public outputAnalyser: AnalyserNode | null = null;
  public inputAnalyser: AnalyserNode | null = null;

  // Static pre-loading for faster connection
  private static preloadedWorklet = false;
  private static preloadPromise: Promise<void> | null = null;

  // Legacy GeminiClient is no longer used; keep a minimal optional interface for back-compat
  constructor(private geminiClient: { state?: string; sendAudio?: (data: ArrayBuffer) => void; sendMuteState?: (muted: boolean) => void } | null) { }

  /**
   * Pre-load the AudioWorklet module during pre-flight.
   * This saves ~200-500ms when the user clicks Talk.
   */
  static async preloadAudioWorklet(): Promise<void> {
    if (GeminiAudioManager.preloadedWorklet) {
      return; // Already preloaded
    }
    if (GeminiAudioManager.preloadPromise) {
      return GeminiAudioManager.preloadPromise; // Already preloading
    }

    GeminiAudioManager.preloadPromise = (async () => {
      try {
        // Create a temporary AudioContext just to load the worklet
        const tempContext = new AudioContext({ sampleRate: 16000 });
        await tempContext.audioWorklet.addModule('/audio-worklet-processor.js');
        await tempContext.close();
        GeminiAudioManager.preloadedWorklet = true;
        devLogger.log('🔊 AudioWorklet pre-loaded during pre-flight');
      } catch (error) {
        devLogger.warn('⚠️ AudioWorklet pre-load failed:', error);
        // Not critical - will load on demand when user clicks Talk
      } finally {
        GeminiAudioManager.preloadPromise = null;
      }
    })();

    return GeminiAudioManager.preloadPromise;
  }

  setClient(client: { state?: string; sendAudio?: (data: ArrayBuffer) => void; sendMuteState?: (muted: boolean) => void }): void {
    this.geminiClient = client;
  }

  // Optional: allow external sinks (e.g., Multimodal Live client) to consume mic frames.
  setFrameSink(cb: ((data: ArrayBuffer) => void) | null): void {
    this.frameSink = cb;
  }

  async startAudioCapture(): Promise<void> {
    // Mobile browsers may require explicit sample rate handling
    // Some browsers (especially iOS Safari) may not support 16kHz AudioContext
    try {
      this.audioContext = new AudioContext({ sampleRate: 16000 });
      devLogger.log('🔊 AudioContext created with 16kHz sample rate');
    } catch (e) {
      // Fallback without explicit sample rate (let browser decide)
      devLogger.warn('⚠️ AudioContext creation with 16kHz failed, using default sample rate');
      this.audioContext = new AudioContext();
    }

    // Initialize output analyser for visualization
    this.outputAnalyser = this.audioContext.createAnalyser();
    this.outputAnalyser.fftSize = 256;
    this.outputAnalyser.smoothingTimeConstant = 0.1;
    this.workletModuleLoaded = false; // Reset module loaded flag for new context

    // Resume audio context if suspended (required by browser autoplay policies)
    // This is critical for mobile browsers which aggressively suspend AudioContext
    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        devLogger.log('🔊 Audio context resumed (was suspended)');
      } catch (e) {
        devLogger.warn('⚠️ Failed to resume AudioContext:', e);
      }
    }

    await this.connectAudioStream();
    devLogger.log('🎤 Audio capture started, context sample rate:', this.audioContext.sampleRate);
  }

  // Helper to detect mobile Safari which has specific audio quirks
  private isMobileSafari(): boolean {
    const ua = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
  }

  // Ensure an AudioContext exists for playback-only scenarios (no mic).
  async ensurePlaybackContext(): Promise<void> {
    if (!this.audioContext) {
      try {
        this.audioContext = new AudioContext({ sampleRate: 24000 });
      } catch (e) {
        // Fallback without explicit sample rate
        this.audioContext = new AudioContext();
      }
      // Initialize output analyser for visualization
      this.outputAnalyser = this.audioContext.createAnalyser();
      this.outputAnalyser.fftSize = 256;
      this.outputAnalyser.smoothingTimeConstant = 0.1;
    }
    if (this.audioContext.state === 'suspended') {
      try { await this.audioContext.resume(); } catch { }
    }
  }

  private async connectAudioStream(): Promise<void> {
    if (this.workletNode) {
      this.disconnectAudioStream();
    }

    // Load the AudioWorklet processor (only once per AudioContext)
    if (!this.workletModuleLoaded) {
      try {
        await this.audioContext!.audioWorklet.addModule('/audio-worklet-processor.js');
        this.workletModuleLoaded = true;
        devLogger.log('✅ AudioWorklet module loaded successfully');
      } catch (error) {
        console.error('❌ Failed to load AudioWorklet processor:', error);
        throw error;
      }
    }

    // Mobile-friendly audio constraints with fallbacks
    // iOS Safari doesn't support sampleRate constraint, so we conditionally include it
    const audioConstraints: MediaTrackConstraints = {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      // Only request sample rate if not iOS Safari (which ignores/rejects it)
      ...(this.isMobileSafari() ? {} : { sampleRate: 16000 })
    };

    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints
      });
      devLogger.log('🎤 getUserMedia succeeded with constraints:', JSON.stringify(audioConstraints));
    } catch (e) {
      // Retry with minimal constraints for browsers that reject specific settings
      devLogger.warn('⚠️ getUserMedia with full constraints failed, retrying with minimal:', e);
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true
      });
      devLogger.log('🎤 getUserMedia succeeded with minimal constraints');
    }

    const source = this.audioContext!.createMediaStreamSource(this.mediaStream);

    // Initialize input analyser for visualization
    this.inputAnalyser = this.audioContext!.createAnalyser();
    this.inputAnalyser.fftSize = 256;
    this.inputAnalyser.smoothingTimeConstant = 0.1;
    source.connect(this.inputAnalyser);

    // Create AudioWorkletNode
    this.workletNode = new AudioWorkletNode(this.audioContext!, 'gemini-audio-processor');

    // Listen for audio data and VAD signals from the worklet
    this.workletNode.port.onmessage = (event) => {
      const { type, data } = event.data;

      if (type === 'audioData') {
        if (this.frameSink) {
          // Forward to external sink (e.g., Multimodal Live input)
          this.frameSink(data);
        } else if (this.geminiClient && this.geminiClient.state === 'connected') {
          // Legacy: send to Gemini websocket
          this.geminiClient.sendAudio(data);
        }
      } else if (type === 'vad') {
        // Bubble up VAD state to UI via a custom event
        const speaking = !!data?.speaking;
        window.dispatchEvent(new CustomEvent('sb-vad-speaking', { detail: { speaking } }));
      }
    };

    // Update soft mute state in worklet
    this.workletNode.port.postMessage({
      type: 'setSoftMuted',
      data: { muted: this.isSoftMuted }
    });

    source.connect(this.workletNode);
    this.workletNode.connect(this.audioContext!.destination);
  }

  private disconnectAudioStream(): void {
    if (this.workletNode) {
      this.workletNode.disconnect();
      this.workletNode = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
  }

  async playAudio(audioData: ArrayBuffer): Promise<void> {
    if (!this.audioContext) {
      // Lazily create a playback context if none exists
      await this.ensurePlaybackContext();
    }

    // Resume audio context if suspended
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
      devLogger.log('🔊 Audio context resumed for playback');
    }

    // Add to queue with cap
    this.audioQueue.push(audioData);
    if (this.audioQueue.length > this.MAX_QUEUE) {
      // Drop oldest chunk(s)
      const drop = this.audioQueue.length - this.MAX_QUEUE;
      if (drop > 0) {
        this.audioQueue.splice(0, drop);
        try { Sentry.addBreadcrumb({ category: 'audio', level: 'warning', message: `audioQueue trimmed by ${drop}` }); } catch { }
      }
    }

    // Start playing if not already playing
    if (!this.isPlaying) {
      this.isPlaying = true;
      this.nextStartTime = this.audioContext.currentTime;
      this.playNextChunk();
    }
  }

  clearAudioQueue(): void {
    devLogger.log('🧹 Clearing audio queue due to connection issue');

    // Stop current sources
    this.currentAudioSources.forEach(source => {
      try {
        source.stop();
      } catch (error) {
        // Source might already be stopped, ignore error
      }
    });
    this.currentAudioSources = [];

    this.audioQueue = [];
    this.isPlaying = false;
    this.nextStartTime = 0;
    this.scheduledUntil = 0;
    this.lastSampleValue = 0;
  }

  stopCurrentAudio(): void {
    devLogger.log('🛑 Stopping current audio playback immediately');

    // Stop all currently playing audio sources
    this.currentAudioSources.forEach(source => {
      try {
        source.stop();
      } catch (error) {
        // Source might already be stopped, ignore error
      }
    });
    this.currentAudioSources = [];

    // Clear queue and reset playback state
    this.audioQueue = [];
    this.isPlaying = false;
    this.nextStartTime = 0;
    this.scheduledUntil = 0;
    this.lastSampleValue = 0;
  }

  private audioChunkCount = 0;
  private scheduledUntil = 0; // Track how far ahead we've scheduled audio
  private readonly SCHEDULE_AHEAD_TIME = 0.3; // Schedule 300ms ahead to prevent gaps
  private readonly MIN_BUFFER_TIME = 0.1; // Start playback when we have 100ms buffered
  private lastSampleValue = 0; // For crossfading between chunks

  private async playNextChunk(): Promise<void> {
    if (!this.audioContext || this.audioQueue.length === 0) {
      this.isPlaying = false;
      this.scheduledUntil = 0;
      this.lastSampleValue = 0;
      return;
    }

    try {
      // Check audio context state
      if (this.audioContext.state === 'suspended') {
        devLogger.warn('⚠️ Audio context suspended, attempting to resume...');
        await this.audioContext.resume();
      }

      const currentTime = this.audioContext.currentTime;

      // Schedule multiple chunks ahead to prevent gaps
      while (this.audioQueue.length > 0 &&
        (this.scheduledUntil - currentTime) < this.SCHEDULE_AHEAD_TIME) {

        const audioData = this.audioQueue.shift()!;
        const pcmData = new Int16Array(audioData);
        const sampleRate = 24000; // Gemini Live audio output sample rate

        if (pcmData.length === 0) {
          continue; // Skip empty chunks
        }

        // Create AudioBuffer for the PCM data
        const audioBuffer = this.audioContext.createBuffer(1, pcmData.length, sampleRate);
        const channelData = audioBuffer.getChannelData(0);

        // Convert Int16 PCM to Float32
        for (let i = 0; i < pcmData.length; i++) {
          channelData[i] = pcmData[i] / 32768.0;
        }

        // Create and schedule the buffer source
        const source = this.audioContext.createBufferSource();
        source.buffer = audioBuffer;

        // Connect to analyser if available, otherwise direct to destination
        if (this.outputAnalyser) {
          source.connect(this.outputAnalyser);
          this.outputAnalyser.connect(this.audioContext.destination);
        } else {
          source.connect(this.audioContext.destination);
        }

        // Track this source so we can stop it if needed
        this.currentAudioSources.push(source);

        // Determine start time - ensure we don't schedule in the past
        let startTime = this.scheduledUntil;
        if (startTime < currentTime) {
          // If we're behind, start immediately but log it
          startTime = currentTime;
          if (this.audioChunkCount % 50 === 0) {
            devLogger.warn(`⚠️ Audio playback fell behind, catching up`);
          }
        }

        source.start(startTime);

        // Update scheduled time
        const duration = audioBuffer.duration;
        this.scheduledUntil = startTime + duration;

        // Clean up source reference when done
        source.onended = () => {
          const index = this.currentAudioSources.indexOf(source);
          if (index > -1) {
            this.currentAudioSources.splice(index, 1);
          }
        };

        this.audioChunkCount++;
        if (this.audioChunkCount % 500 === 0) {
          devLogger.info(`🔊 Played ${this.audioChunkCount} audio chunks so far`);
        }
      }

      // Log drift periodically
      if (this.audioChunkCount % 200 === 0) {
        const drift = this.scheduledUntil - currentTime;
        devLogger.log(`⏱️ playback buffer ${drift.toFixed(3)}s, queue=${this.audioQueue.length}`);
      }

      // Check if more audio is in queue or might arrive soon
      if (this.audioQueue.length > 0) {
        // More chunks available, schedule immediately
        setTimeout(() => this.playNextChunk(), 0);
      } else {
        // Queue empty, check again after some time
        const timeUntilEmpty = Math.max(0, (this.scheduledUntil - currentTime - 0.05) * 1000);
        setTimeout(() => {
          if (this.audioQueue.length > 0) {
            this.playNextChunk();
          } else {
            this.isPlaying = false;
            this.scheduledUntil = 0;
          }
        }, timeUntilEmpty);
      }

    } catch (error) {
      console.error('❌ Error playing audio chunk:', error);
      try { Sentry.captureException(error as any, { tags: { phase: 'audio_playback' } }); } catch { }
      // Continue with next chunk even if this one failed
      setTimeout(() => this.playNextChunk(), 0);
    }
  }

  async mute(): Promise<void> {
    if (this.isMuted) return;

    this.isMuted = true;
    this.isSoftMuted = true;
    devLogger.log('🔇 Audio input muted - sending silence data (preserves VAD and connection)');

    // Update soft mute state in worklet (sends silence instead of mic audio)
    if (this.workletNode) {
      this.workletNode.port.postMessage({
        type: 'setSoftMuted',
        data: { muted: true }
      });
    }

    // Signal backend about mute state but keep connection alive
    this.geminiClient?.sendMuteState(true);
  }



  async unmute(): Promise<void> {
    if (!this.isMuted) return;

    this.isMuted = false;
    this.isSoftMuted = false;
    devLogger.log('🎤 Audio input unmuted - resuming microphone audio stream');

    try {
      // Update soft mute state in worklet (resume sending mic audio)
      if (this.workletNode) {
        this.workletNode.port.postMessage({
          type: 'setSoftMuted',
          data: { muted: false }
        });
      }

      // Signal backend about unmute state
      this.geminiClient?.sendMuteState(false);
    } catch (error) {
      console.error('❌ Error resuming audio stream after unmute:', error);
      this.isMuted = true; // Revert to muted state on error
      this.isSoftMuted = false;
      throw error;
    }
  }

  get muted(): boolean {
    return this.isMuted;
  }

  stop(): void {
    // Stop all current audio sources first
    this.stopCurrentAudio();

    // Cleanup audio processing
    this.disconnectAudioStream();
    this.audioContext?.close();

    // Reset all state
    this.audioQueue = [];
    this.isPlaying = false;
    this.isMuted = false;
    this.isSoftMuted = false;
    this.pendingResponseCompletion = false;
    this.currentAudioSources = [];
    this.audioContext = null;
  }
} 
